/**
 * Priority-driven Main-tab layout for Pro 3EM, single-channel emeters, and pm1 power meters.
 * npm test src/utest/energy/emeter_priority_layout.test.ts
 */
import type { EmeterMainMetric } from 'src/helpers/emeter_main_metric';

export const EMETER_IMBALANCE_THRESHOLD = 15;

/** Matches `compute-device-state` uiState.type values used for overlay inference. */
export const EMETER_UI_OFFLINE = 0;
export const EMETER_UI_PENDING = 1;
export const EMETER_UI_REBOOTING = 2;
export const EMETER_UI_UPDATING = 3;
export const EMETER_UI_LOADING = 8;
export const EMETER_UI_WIFI_UPDATING = 10;
export const EMETER_UI_ASSOCIATING = 11;

export type EmeterGridMode = 'measurement' | 'export' | 'imbalance';

export type EmeterOverlay =
	| 'offline'
	| 'loading'
	| 'firmware'
	| 'overheat'
	| 'overpower'
	| 'overvoltage';

export type EmeterPriorityDevForce = 'auto' | EmeterGridMode | EmeterOverlay;

export type EmeterPriorityCardPriority = 'primary' | 'secondary' | 'tertiary' | 'compact';

export type EmeterPriorityCardTone = 'warning' | 'danger' | 'success' | 'muted' | 'info';

export type EmeterPriorityLabel = {
	key: string;
	default: string;
	values?: Record<string, string | number>;
};

export type EmeterPriorityCard = {
	id: string;
	value: string;
	unit?: string;
	numericValue?: number;
	label: EmeterPriorityLabel;
	/** Pre-resolved label when i18n would overwrite a user-configured phase name. */
	labelText?: string;
	icon: string;
	priority: EmeterPriorityCardPriority;
	tone?: EmeterPriorityCardTone;
	stale?: boolean;
	disabled?: boolean;
	chart?: number[];
	accentColor?: string;
	kind?: 'metric' | 'status';
	sortValue: number;
};

export type EmeterPriorityBar = {
	label: string;
	progress: number;
	value: string;
	accentColor: string;
};

export type EmeterPriorityReadings = {
	totalActPower: number;
	totalCurrent: number;
	phasePowers: [number, number, number];
	phaseCurrents: [number, number, number];
	voltages: [number, number, number];
	nCurrent: number | null;
	hasNeutral: boolean;
	pf: number | null;
	freq: number | null;
	totalAct: number | null;
	totalActRet: number | null;
	temperature: number | null;
};

export type EmeterPriorityLayoutInput = {
	isMonophase: boolean;
	metric: EmeterMainMetric;
	online: boolean;
	uiStateType?: number;
	isLoading?: boolean;
	errors?: string[];
	force?: EmeterPriorityDevForce;
	channelNames?: Array<string | undefined>;
	readings: EmeterPriorityReadings | null;
	charts?: Partial<Record<string, number[]>>;
	/** Hottest-phase takeover. Missing/false = off. HUD force still applies. */
	imbalanceEmphasis?: boolean;
};

export type EmeterPriorityLayout = {
	mode: EmeterGridMode;
	overlay: EmeterOverlay | null;
	cards: EmeterPriorityCard[];
	dockCard: EmeterPriorityCard | null;
	phaseBars: EmeterPriorityBar[];
	phaseStatus: {
		label: EmeterPriorityLabel;
		tone: 'success' | 'warning';
	} | null;
	imbalancePercent: number;
	showPhaseBars: boolean;
};

/** Slot identity only — watt/spark ticks must not change this or they cancel FLIP. */
export const emeterPriorityFlipKey = (layout: EmeterPriorityLayout): string =>
	[
		layout.mode,
		layout.overlay ?? '',
		layout.dockCard?.id ?? '',
		...layout.cards.map((card) => `${card.id}:${card.priority}`),
	].join('|');

const PHASES = ['A', 'B', 'C'] as const;
const PHASE_ACCENTS = [
	'var(--color-theme-phase-a)',
	'var(--color-theme-phase-b)',
	'var(--color-theme-phase-c)',
] as const;
/** Solid gray seed — lighting cannot parse color-mix disabled text. */
const OVERLAY_MUTE_ACCENT = 'var(--color-theme-fg-02)';
const METRIC_ACCENTS: Record<string, string> = {
	hero: 'var(--color-theme-success)',
	power: 'var(--color-theme-success)',
	energy: 'var(--color-theme-success)',
	returned: 'var(--color-theme-success)',
	phaseA: PHASE_ACCENTS[0],
	phaseB: PHASE_ACCENTS[1],
	phaseC: PHASE_ACCENTS[2],
	neutral: 'var(--color-theme-metric-neutral)',
	voltage: 'var(--color-theme-metric-voltage)',
	voltageAverage: 'var(--color-theme-metric-voltage)',
	current: 'var(--color-theme-metric-current)',
	factor: 'var(--color-theme-metric-factor)',
	frequency: 'var(--color-theme-metric-frequency)',
	temperature: 'var(--color-theme-metric-temperature)',
};
const PHASE_ICONS = ['fa-solid fa-a', 'fa-solid fa-b', 'fa-solid fa-c'] as const;
const PRIORITY_RANK: Record<EmeterPriorityCardPriority, number> = {
	primary: 0,
	secondary: 1,
	tertiary: 2,
	compact: 3,
};

const GRID_FORCES: EmeterGridMode[] = ['measurement', 'export', 'imbalance'];
const OVERLAY_FORCES: EmeterOverlay[] = [
	'offline',
	'loading',
	'firmware',
	'overheat',
	'overpower',
	'overvoltage',
];

const LOADING_UI_TYPES = new Set([
	EMETER_UI_PENDING,
	EMETER_UI_REBOOTING,
	EMETER_UI_LOADING,
	EMETER_UI_WIFI_UPDATING,
	EMETER_UI_ASSOCIATING,
]);

export const EMETER_PRIORITY_DEV_OPTIONS: {
	id: EmeterPriorityDevForce;
	label: string;
}[] = [
	{ id: 'auto', label: 'Auto' },
	{ id: 'measurement', label: 'Measurement' },
	{ id: 'export', label: 'Export' },
	{ id: 'imbalance', label: 'Imbalance' },
	{ id: 'offline', label: 'Offline' },
	{ id: 'loading', label: 'Loading' },
	{ id: 'firmware', label: 'Firmware' },
	{ id: 'overheat', label: 'Overheat' },
	{ id: 'overpower', label: 'Overpower' },
	{ id: 'overvoltage', label: 'Overvoltage' },
];

export const emeterPriorityDevOptionsFor = (isSingleChannel: boolean) =>
	isSingleChannel
		? EMETER_PRIORITY_DEV_OPTIONS.filter((option) => option.id !== 'imbalance')
		: EMETER_PRIORITY_DEV_OPTIONS;

export const isEmeterGridForce = (force: EmeterPriorityDevForce | undefined): force is EmeterGridMode =>
	Boolean(force && GRID_FORCES.includes(force as EmeterGridMode));

export const isEmeterOverlayForce = (
	force: EmeterPriorityDevForce | undefined,
): force is EmeterOverlay => Boolean(force && OVERLAY_FORCES.includes(force as EmeterOverlay));

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const asNumber = (value: unknown): number | null =>
	typeof value === 'number' && !Number.isNaN(value) ? value : null;

const asTemperatureC = (value: unknown): number | null => {
	const direct = asNumber(value);
	if (direct != null) return direct;
	const record = asRecord(value);
	return record ? asNumber(record.tC) : null;
};

const formatScaled = (value: number, unit: string): { value: string; unit: string } => {
	const sign = value < 0 ? '-' : '';
	let next = Math.abs(value);
	let nextUnit = unit;
	if (next > 999) {
		next = next / 1000;
		nextUnit = `k${unit}`;
	}
	const rounded = Math.round(next * 100) / 100;
	return { value: `${sign}${rounded}`, unit: nextUnit };
};

const formatFixed = (value: number, digits: number): string => {
	const factor = 10 ** digits;
	return (Math.round(value * factor) / factor).toFixed(digits);
};

const phaseName = (index: number, channelNames?: Array<string | undefined>): string =>
	channelNames?.[index] || `Phase ${PHASES[index]}`;

const normalizeError = (error: string): string => {
	const colonIndex = error.indexOf(':');
	if (colonIndex === -1) return error;
	const suffix = error.slice(colonIndex + 1);
	return /^\d+$/.test(suffix) ? error.slice(0, colonIndex) : error;
};

const pushErrors = (target: string[], value: unknown) => {
	if (Array.isArray(value)) {
		value.forEach((item) => {
			if (typeof item === 'string' && item) target.push(normalizeError(item));
		});
		return;
	}
	if (typeof value === 'string' && value) target.push(normalizeError(value));
};

/** Collect protection / fault codes from em / em1 / pm1 status components. */
export const collectEmeterProtectionErrors = (status: Record<string, unknown> | undefined): string[] => {
	if (!status) return [];
	const errors: string[] = [];
	for (const key of Object.keys(status)) {
		if (!/^(em1?|pm1):\d+$/.test(key)) continue;
		const component = asRecord(status[key]);
		if (!component) continue;
		['errors', 'a_errors', 'b_errors', 'c_errors', 'n_errors', 'flags'].forEach((field) => {
			pushErrors(errors, component[field]);
		});
	}
	return errors;
};

export const extractEmeterReadings = (
	status: Record<string, unknown> | undefined,
	isMonophase: boolean,
	channel = 0,
): EmeterPriorityReadings | null => {
	if (!status) return null;

	if (isMonophase) {
		const em1 = asRecord(status[`em1:${channel}`]);
		const pm1 = asRecord(status[`pm1:${channel}`]);
		const meter = em1 ?? pm1;
		const em1data = asRecord(status[`em1data:${channel}`]);
		if (!meter) return null;
		const power = asNumber(meter.act_power) ?? asNumber(meter.apower) ?? 0;
		const current = asNumber(meter.current) ?? 0;
		const voltage = asNumber(meter.voltage) ?? 0;
		const aenergy = asRecord(meter.aenergy);
		const retAenergy = asRecord(meter.ret_aenergy);
		return {
			totalActPower: power,
			totalCurrent: current,
			phasePowers: [power, 0, 0],
			phaseCurrents: [current, 0, 0],
			voltages: [voltage, 0, 0],
			nCurrent: null,
			hasNeutral: false,
			pf: asNumber(meter.pf),
			freq: asNumber(meter.freq),
			totalAct: asNumber(em1data?.total_act_energy) ?? asNumber(aenergy?.total),
			totalActRet: asNumber(em1data?.total_act_ret_energy) ?? asNumber(retAenergy?.total),
			temperature:
				asTemperatureC(meter.temperature) ??
				asTemperatureC(status.temperature) ??
				asTemperatureC(status['temperature:0']),
		};
	}

	const em = asRecord(status[`em:${channel}`]) ?? asRecord(status['em:0']);
	const emdata = asRecord(status[`emdata:${channel}`]) ?? asRecord(status['emdata:0']);
	if (!em) return null;

	const phasePowers: [number, number, number] = [
		asNumber(em.a_act_power) ?? 0,
		asNumber(em.b_act_power) ?? 0,
		asNumber(em.c_act_power) ?? 0,
	];
	const phaseCurrents: [number, number, number] = [
		asNumber(em.a_current) ?? 0,
		asNumber(em.b_current) ?? 0,
		asNumber(em.c_current) ?? 0,
	];
	const voltages: [number, number, number] = [
		asNumber(em.a_voltage) ?? 0,
		asNumber(em.b_voltage) ?? 0,
		asNumber(em.c_voltage) ?? 0,
	];
	const calibrated = Array.isArray(em.user_calibrated_phase)
		? (em.user_calibrated_phase as string[])
		: [];
	const nCurrent = asNumber(em.n_current);
	const pfs = [asNumber(em.a_pf), asNumber(em.b_pf), asNumber(em.c_pf)].filter(
		(value): value is number => value != null,
	);

	return {
		totalActPower:
			asNumber(em.total_act_power) ?? phasePowers.reduce((sum, value) => sum + value, 0),
		totalCurrent: asNumber(em.total_current) ?? phaseCurrents.reduce((sum, value) => sum + value, 0),
		phasePowers,
		phaseCurrents,
		voltages,
		nCurrent,
		hasNeutral: calibrated.includes('n') && nCurrent != null,
		pf: pfs.length ? pfs.reduce((sum, value) => sum + value, 0) / pfs.length : asNumber(em.pf),
		freq: asNumber(em.a_freq) ?? asNumber(em.b_freq) ?? asNumber(em.c_freq) ?? asNumber(em.freq),
		totalAct: asNumber(emdata?.total_act),
		totalActRet: asNumber(emdata?.total_act_ret),
		temperature:
			asTemperatureC(em.temperature) ??
			asTemperatureC(status.temperature) ??
			asTemperatureC(status['temperature:0']),
	};
};

export const inferEmeterOverlay = ({
	online,
	uiStateType,
	isLoading,
	errors = [],
}: {
	online: boolean;
	uiStateType?: number;
	isLoading?: boolean;
	errors?: string[];
}): EmeterOverlay | null => {
	if (!online || uiStateType === EMETER_UI_OFFLINE) return 'offline';

	const codes = new Set(errors.map((error) => error.toLowerCase()));
	const has = (...needles: string[]) => needles.some((needle) => codes.has(needle));

	if (has('overpower')) return 'overpower';
	if (has('overtemp', 'overtemperature', 'overheat')) return 'overheat';
	if (has('overvoltage')) return 'overvoltage';
	if (uiStateType === EMETER_UI_UPDATING) return 'firmware';
	if (isLoading || (uiStateType != null && LOADING_UI_TYPES.has(uiStateType))) return 'loading';
	return null;
};

export const inferEmeterGridMode = (
	readings: EmeterPriorityReadings,
	isMonophase: boolean,
	imbalanceEmphasis = false,
): EmeterGridMode => {
	if (!isMonophase && imbalanceEmphasis) {
		const absPowers = readings.phasePowers.map((value) => Math.abs(value));
		const max = Math.max(...absPowers, 0);
		const min = Math.min(...absPowers);
		const imbalancePercent = max > 0 ? Math.round(((max - min) / max) * 100) : 0;
		if (imbalancePercent > EMETER_IMBALANCE_THRESHOLD) return 'imbalance';
	}
	if (readings.totalActPower < 0) return 'export';
	return 'measurement';
};

export const applyForcedEmeterReadings = (
	readings: EmeterPriorityReadings,
	force: EmeterPriorityDevForce | undefined,
	isMonophase: boolean,
): EmeterPriorityReadings => {
	if (!isEmeterGridForce(force) || (isMonophase && force === 'imbalance')) return readings;

	if (force === 'export') {
		if (isMonophase) {
			const power = -Math.abs(readings.phasePowers[0] || readings.totalActPower || 1080);
			const current = -Math.abs(readings.phaseCurrents[0] || readings.totalCurrent || 4.7);
			return {
				...readings,
				phasePowers: [power, 0, 0],
				phaseCurrents: [current, 0, 0],
				totalActPower: power,
				totalCurrent: current,
				totalActRet: Math.max(readings.totalActRet ?? 0, 21500),
			};
		}
		const phasePowers: [number, number, number] = [
			-Math.abs(readings.phasePowers[0] || 1080),
			-Math.abs(readings.phasePowers[1] || 1130),
			-Math.abs(readings.phasePowers[2] || 1050),
		];
		const phaseCurrents: [number, number, number] = [
			-Math.abs(readings.phaseCurrents[0] || 4.7),
			-Math.abs(readings.phaseCurrents[1] || 4.9),
			-Math.abs(readings.phaseCurrents[2] || 4.5),
		];
		return {
			...readings,
			phasePowers,
			phaseCurrents,
			totalActPower: phasePowers.reduce((sum, value) => sum + value, 0),
			totalCurrent: phaseCurrents.reduce((sum, value) => sum + value, 0),
			totalActRet: Math.max(readings.totalActRet ?? 0, 21500),
		};
	}

	if (force === 'imbalance') {
		const hottest = Math.max(
			Math.abs(readings.phasePowers[0]),
			Math.abs(readings.phasePowers[1]),
			5230,
		);
		const phasePowers: [number, number, number] = [
			Math.abs(readings.phasePowers[0] || 2110),
			Math.abs(readings.phasePowers[1] || 2340),
			hottest,
		];
		const phaseCurrents: [number, number, number] = [
			Math.abs(readings.phaseCurrents[0] || 9.2),
			Math.abs(readings.phaseCurrents[1] || 10.2),
			Math.max(Math.abs(readings.phaseCurrents[2]), 22.7),
		];
		return {
			...readings,
			phasePowers,
			phaseCurrents,
			totalActPower: phasePowers.reduce((sum, value) => sum + value, 0),
			totalCurrent: phaseCurrents.reduce((sum, value) => sum + value, 0),
			nCurrent: Math.max(readings.nCurrent ?? 0, 8.74),
			hasNeutral: true,
		};
	}

	return readings;
};

const metricCard = (
	partial: Omit<EmeterPriorityCard, 'kind' | 'sortValue'> & {
		sortValue: number;
	},
): EmeterPriorityCard => ({
	kind: 'metric',
	...partial,
	accentColor: partial.accentColor ?? METRIC_ACCENTS[partial.id],
});

const hottestPhaseIndex = (values: number[]): number => {
	let hottest = 0;
	let max = -1;
	values.forEach((value, index) => {
		const abs = Math.abs(value);
		if (abs > max) {
			max = abs;
			hottest = index;
		}
	});
	return hottest;
};

const imbalancePercentFor = (values: number[]): number => {
	const abs = values.map((value) => Math.abs(value));
	const max = Math.max(...abs, 0);
	const min = Math.min(...abs);
	return max > 0 ? Math.round(((max - min) / max) * 100) : 0;
};

const buildDockCard = (
	overlay: EmeterOverlay,
	readings: EmeterPriorityReadings | null,
	metric: EmeterMainMetric,
	isMonophase: boolean,
): EmeterPriorityCard => {
	if (overlay === 'offline') {
		return {
			id: 'connection',
			value: 'Offline',
			label: {
				key: 'label_emeter_device_unavailable',
				default: 'Device unavailable',
			},
			icon: 'fa-solid fa-cloud-slash',
			priority: 'primary',
			tone: 'muted',
			accentColor: OVERLAY_MUTE_ACCENT,
			kind: 'status',
			sortValue: 0,
		};
	}
	if (overlay === 'loading') {
		return {
			id: 'connection',
			value: 'Connecting…',
			label: {
				key: 'label_emeter_cloud_connection',
				default: 'Cloud connection',
			},
			icon: 'fa-solid fa-wifi',
			priority: 'primary',
			tone: 'muted',
			accentColor: OVERLAY_MUTE_ACCENT,
			kind: 'status',
			sortValue: 0,
		};
	}
	if (overlay === 'firmware') {
		return {
			id: 'status',
			value: 'Updating firmware',
			label: { key: 'label_emeter_status', default: 'Status' },
			icon: 'fa-solid fa-rotate',
			priority: 'primary',
			tone: 'info',
			kind: 'status',
			sortValue: 0,
		};
	}
	if (overlay === 'overheat') {
		const temperature = readings?.temperature ?? 78.4;
		return {
			id: 'temperature',
			value: formatFixed(temperature, 1),
			unit: '°C',
			label: { key: 'label_emeter_overheating', default: 'Overheating' },
			icon: 'fa-solid fa-temperature-half',
			priority: 'primary',
			tone: 'warning',
			accentColor: 'var(--color-theme-climate-hot)',
			kind: 'metric',
			sortValue: 0,
		};
	}
	if (overlay === 'overvoltage') {
		const voltages = readings?.voltages ?? [268, 268, 268];
		const value = isMonophase
			? voltages[0] || 268
			: voltages.reduce((sum, next) => sum + next, 0) / 3;
		return {
			id: isMonophase ? 'voltage' : 'voltageAverage',
			value: formatFixed(value, 1),
			unit: 'V',
			label: { key: 'label_emeter_overvoltage', default: 'Overvoltage' },
			icon: 'fa-solid fa-wave-square',
			priority: 'primary',
			tone: 'warning',
			kind: 'metric',
			sortValue: 0,
		};
	}

	const raw = metric === 'current' ? (readings?.totalCurrent ?? 0) : (readings?.totalActPower ?? 0);
	const formatted = formatScaled(raw, metric === 'current' ? 'A' : 'W');
	return {
		id: isMonophase ? 'power' : 'hero',
		value: formatted.value,
		unit: formatted.unit,
		label: { key: 'label_emeter_overpower', default: 'Overpower' },
		icon: 'fa-solid fa-bolt',
		priority: 'primary',
		tone: 'danger',
		kind: 'metric',
		sortValue: 0,
	};
};

const assignTriphasePriorities = (
	cards: EmeterPriorityCard[],
	mode: EmeterGridMode,
	phaseValues: number[],
	hottestIndex: number,
	neutralElevated: boolean,
) => {
	const byId = new Map(cards.map((card) => [card.id, card]));
	const phaseOrder = [0, 1, 2]
		.map((index) => ({ index, abs: Math.abs(phaseValues[index]) }))
		.sort((left, right) => right.abs - left.abs);

	cards.forEach((card) => {
		card.priority = 'tertiary';
		card.tone = undefined;
	});

	if (mode === 'imbalance') {
		const hottest = byId.get(`phase${PHASES[hottestIndex]}`);
		if (hottest) {
			hottest.priority = 'primary';
			hottest.tone = 'warning';
			hottest.accentColor = undefined;
			hottest.sortValue = 0;
		}
		const hero = byId.get('hero');
		if (hero) hero.priority = 'secondary';
		phaseOrder.forEach((entry, order) => {
			if (entry.index === hottestIndex) return;
			const card = byId.get(`phase${PHASES[entry.index]}`);
			if (card) {
				card.priority = order === 1 ? 'secondary' : 'tertiary';
				card.sortValue = 10 + order;
			}
		});
		const neutral = byId.get('neutral');
		if (neutral && neutralElevated) {
			neutral.priority = 'secondary';
			neutral.tone = 'warning';
		}
		return;
	}

	if (mode === 'export') {
		const returned = byId.get('returned');
		if (returned) {
			returned.priority = 'primary';
			returned.tone = 'success';
			returned.accentColor = 'var(--color-theme-success)';
			returned.sortValue = 0;
		}
		const hero = byId.get('hero');
		if (hero) {
			hero.priority = 'secondary';
			hero.tone = 'success';
			hero.label = {
				key: 'label_emeter_net_power_exporting',
				default: 'Net power · exporting',
			};
		}
		phaseOrder.forEach((entry, order) => {
			const card = byId.get(`phase${PHASES[entry.index]}`);
			if (card) {
				card.priority = order < 2 ? 'secondary' : 'tertiary';
				card.sortValue = 10 + order;
			}
		});
		return;
	}

	const hero = byId.get('hero');
	if (hero) hero.priority = 'primary';
	phaseOrder.forEach((entry, order) => {
		const card = byId.get(`phase${PHASES[entry.index]}`);
		if (card) {
			card.priority = order < 2 ? 'secondary' : 'tertiary';
			card.sortValue = 10 + order;
		}
	});
};

const assignSingleChannelPriorities = (
	cards: EmeterPriorityCard[],
	mode: EmeterGridMode,
	metric: EmeterMainMetric,
) => {
	const byId = new Map(cards.map((card) => [card.id, card]));
	cards.forEach((card) => {
		card.priority = 'tertiary';
		card.tone = undefined;
	});

	const power = byId.get('power');
	const current = byId.get('current');
	const voltage = byId.get('voltage');
	const returned = byId.get('returned');

	if (voltage) {
		voltage.priority = 'secondary';
		voltage.sortValue = 2;
	}

	if (mode === 'export') {
		if (returned) {
			returned.priority = 'primary';
			returned.tone = 'success';
			returned.accentColor = 'var(--color-theme-success)';
			returned.sortValue = 0;
			if (power) {
				power.priority = 'secondary';
				power.tone = 'success';
				power.label = {
					key: 'label_emeter_net_power_exporting',
					default: 'Net power · exporting',
				};
				power.sortValue = 1;
			}
		} else if (power) {
			power.priority = 'primary';
			power.tone = 'success';
			power.label = {
				key: 'label_emeter_net_power_exporting',
				default: 'Net power · exporting',
			};
			power.sortValue = 0;
		}
		if (current) {
			current.priority = 'secondary';
			current.sortValue = 3;
		}
		return;
	}

	if (metric === 'current') {
		if (current) {
			current.priority = 'primary';
			current.sortValue = 0;
		}
		if (power) {
			power.priority = 'secondary';
			power.sortValue = 1;
		}
		return;
	}

	if (power) {
		power.priority = 'primary';
		power.sortValue = 0;
	}
	if (current) {
		current.priority = 'secondary';
		current.sortValue = 1;
	}
};

/**
 * Builds the sorted card list, dock overlay, and phase-health bars for Pro 3EM and Pro EM 50.
 */
export const buildEmeterPriorityLayout = (input: EmeterPriorityLayoutInput): EmeterPriorityLayout => {
	const force = input.force ?? 'auto';
	const overlay = isEmeterOverlayForce(force)
		? force
		: inferEmeterOverlay({
				online: input.online,
				uiStateType: input.uiStateType,
				isLoading: input.isLoading,
				errors: input.errors,
			});

	const baseReadings = input.readings
		? applyForcedEmeterReadings(input.readings, force, input.isMonophase)
		: null;
	const mode: EmeterGridMode = isEmeterGridForce(force)
		? input.isMonophase && force === 'imbalance'
			? 'measurement'
			: force
		: baseReadings
			? inferEmeterGridMode(baseReadings, input.isMonophase, Boolean(input.imbalanceEmphasis))
			: 'measurement';

	const demotePrimary = overlay === 'offline' || overlay === 'loading' || overlay === 'firmware';
	const metric = input.metric;
	const charts = input.charts ?? {};
	const empty: EmeterPriorityLayout = {
		mode,
		overlay,
		cards: [],
		dockCard: overlay ? buildDockCard(overlay, baseReadings, metric, input.isMonophase) : null,
		phaseBars: [],
		phaseStatus: null,
		imbalancePercent: 0,
		showPhaseBars: false,
	};

	if (!baseReadings) return empty;

	const phaseValues = metric === 'current' ? baseReadings.phaseCurrents : baseReadings.phasePowers;
	const imbalancePercent = imbalancePercentFor(phaseValues);
	const hottestIndex = hottestPhaseIndex(phaseValues);
	const unit = metric === 'current' ? 'A' : 'W';
	const cards: EmeterPriorityCard[] = [];

	if (input.isMonophase) {
		const powerRaw = baseReadings.totalActPower;
		const currentRaw = baseReadings.totalCurrent;
		const power = formatScaled(powerRaw, 'W');
		const current = formatScaled(currentRaw, 'A');
		cards.push(
			metricCard({
				id: 'power',
				value: power.value,
				unit: power.unit,
				numericValue: powerRaw,
				label: {
					key: 'label_emeter_active_power',
					default: 'Active power',
				},
				icon: 'fa-solid fa-bolt',
				priority: 'primary',
				sortValue: 0,
			}),
		);
		cards.push(
			metricCard({
				id: 'current',
				value: current.value,
				unit: current.unit,
				numericValue: currentRaw,
				label: { key: 'label_emeter_current', default: 'Current' },
				icon: 'fa-solid fa-gauge-high',
				priority: 'secondary',
				sortValue: 1,
			}),
		);
		cards.push(
			metricCard({
				id: 'voltage',
				value: formatFixed(baseReadings.voltages[0], 1),
				unit: 'V',
				label: { key: 'label_voltage', default: 'Voltage' },
				icon: 'fa-solid fa-wave-square',
				priority: 'secondary',
				sortValue: 2,
			}),
		);
		if (baseReadings.totalAct != null) {
			const energy = formatScaled(baseReadings.totalAct, 'Wh');
			cards.push(
				metricCard({
					id: 'energy',
					value: energy.value,
					unit: energy.unit,
					label: {
						key: 'label_emeter_total_energy',
						default: 'Total energy',
					},
					icon: 'fa-solid fa-chart-area',
					priority: 'tertiary',
					sortValue: 10,
				}),
			);
		}
		if (baseReadings.totalActRet != null) {
			const returned = formatScaled(baseReadings.totalActRet, 'Wh');
			cards.push(
				metricCard({
					id: 'returned',
					value: returned.value,
					unit: returned.unit,
					numericValue: baseReadings.totalActRet ?? undefined,
					label: {
						key: 'label_total_returned',
						default: 'Total returned',
					},
					icon: 'fa-solid fa-arrow-rotate-left',
					priority: 'tertiary',
					sortValue: 11,
				}),
			);
		}
		if (baseReadings.pf != null) {
			cards.push(
				metricCard({
					id: 'factor',
					value: formatFixed(baseReadings.pf, 2),
					label: {
						key: 'label_emeter_power_factor',
						default: 'Power factor',
					},
					icon: 'fa-solid fa-circle-half-stroke',
					priority: 'tertiary',
					sortValue: 12,
				}),
			);
		}
		if (baseReadings.freq != null) {
			cards.push(
				metricCard({
					id: 'frequency',
					value: formatFixed(baseReadings.freq, 2),
					unit: 'Hz',
					label: { key: 'label_frequency', default: 'Frequency' },
					icon: 'fa-solid fa-signal',
					priority: 'tertiary',
					sortValue: 13,
				}),
			);
		}
		if (baseReadings.temperature != null) {
			cards.push(
				metricCard({
					id: 'temperature',
					value: formatFixed(baseReadings.temperature, 1),
					unit: '°C',
					label: {
						key: 'label_emeter_device_temperature',
						default: 'Device temperature',
					},
					icon: 'fa-solid fa-temperature-half',
					priority: 'tertiary',
					sortValue: 14,
				}),
			);
		}
		assignSingleChannelPriorities(cards, mode, metric);
		const primary = cards.find((card) => card.priority === 'primary');
		if (primary) primary.chart = charts[primary.id] ?? primary.chart;
	} else {
		const heroRaw = metric === 'current' ? baseReadings.totalCurrent : baseReadings.totalActPower;
		const hero = formatScaled(heroRaw, unit);
		cards.push(
			metricCard({
				id: 'hero',
				value: hero.value,
				unit: hero.unit,
				numericValue: heroRaw,
				label:
					metric === 'current'
						? {
								key: 'label_emeter_total_current',
								default: 'Total current',
							}
						: {
								key: 'label_emeter_total_power',
								default: 'Total power',
							},
				icon: 'fa-solid fa-bolt',
				priority: 'primary',
				chart: charts.hero,
				sortValue: 0,
			}),
		);

		PHASES.forEach((phase, index) => {
			const formatted = formatScaled(phaseValues[index], unit);
			const percentHigh = mode === 'imbalance' && index === hottestIndex ? imbalancePercent : 0;
			const customName = input.channelNames?.[index];
			cards.push(
				metricCard({
					id: `phase${phase}`,
					value: formatted.value,
					unit: formatted.unit,
					numericValue: phaseValues[index],
					label: percentHigh
						? {
								key: 'label_emeter_phase_high',
								default: 'Phase {phase} · {percent}% high',
								values: {
									phase: customName || phase,
									percent: percentHigh,
								},
							}
						: {
								key: 'energy_monitoring_phase_label',
								default: 'Phase {phase}',
								values: { phase },
							},
					labelText: !percentHigh && customName ? customName : undefined,
					icon: PHASE_ICONS[index],
					priority: 'secondary',
					chart: undefined,
					accentColor: PHASE_ACCENTS[index],
					sortValue: 10 + index,
				}),
			);
		});

		if (baseReadings.hasNeutral && baseReadings.nCurrent != null) {
			cards.push(
				metricCard({
					id: 'neutral',
					value: formatFixed(baseReadings.nCurrent, 2),
					unit: 'A',
					label: {
						key: 'label_power_n_phase',
						default: 'Neutral current',
					},
					icon: 'fa-solid fa-n',
					priority: 'tertiary',
					sortValue: 20,
				}),
			);
		}

		const avgVoltage = baseReadings.voltages.reduce((sum, value) => sum + value, 0) / 3;
		cards.push(
			metricCard({
				id: 'voltageAverage',
				value: formatFixed(avgVoltage, 1),
				unit: 'V',
				label: {
					key: 'label_emeter_average_voltage',
					default: 'Average voltage',
				},
				icon: 'fa-solid fa-wave-square',
				priority: 'tertiary',
				sortValue: 21,
			}),
		);

		if (baseReadings.pf != null) {
			cards.push(
				metricCard({
					id: 'factor',
					value: formatFixed(baseReadings.pf, 2),
					label: {
						key: 'label_emeter_power_factor',
						default: 'Power factor',
					},
					icon: 'fa-solid fa-circle-half-stroke',
					priority: 'tertiary',
					sortValue: 22,
				}),
			);
		}

		if (baseReadings.totalAct != null) {
			const energy = formatScaled(baseReadings.totalAct, 'Wh');
			cards.push(
				metricCard({
					id: 'energy',
					value: energy.value,
					unit: energy.unit,
					label: {
						key: 'label_emeter_total_energy',
						default: 'Total energy',
					},
					icon: 'fa-solid fa-chart-area',
					priority: 'tertiary',
					sortValue: 23,
				}),
			);
		}

		if (baseReadings.totalActRet != null) {
			const returned = formatScaled(baseReadings.totalActRet, 'Wh');
			cards.push(
				metricCard({
					id: 'returned',
					value: returned.value,
					unit: returned.unit,
					numericValue: baseReadings.totalActRet ?? undefined,
					label: {
						key: 'label_total_returned',
						default: 'Total returned',
					},
					icon: 'fa-solid fa-arrow-rotate-left',
					priority: 'tertiary',
					sortValue: 24,
				}),
			);
		}

		if (baseReadings.freq != null) {
			cards.push(
				metricCard({
					id: 'frequency',
					value: formatFixed(baseReadings.freq, 2),
					unit: 'Hz',
					label: { key: 'label_frequency', default: 'Frequency' },
					icon: 'fa-solid fa-signal',
					priority: 'tertiary',
					sortValue: 25,
				}),
			);
		}

		const maxPhaseCurrent = Math.max(
			...baseReadings.phaseCurrents.map((value) => Math.abs(value)),
			0,
		);
		const neutralElevated =
			baseReadings.hasNeutral &&
			baseReadings.nCurrent != null &&
			(baseReadings.nCurrent > 1 ||
				(maxPhaseCurrent > 0 && baseReadings.nCurrent > maxPhaseCurrent * 0.15));

		assignTriphasePriorities(cards, mode, phaseValues, hottestIndex, Boolean(neutralElevated));

		const primary = cards.find((card) => card.priority === 'primary');
		if (primary && (mode === 'imbalance' || mode === 'export' || mode === 'measurement')) {
			primary.chart = charts[primary.id] ?? charts.hero ?? primary.chart;
		}
	}

	const dockCard = overlay ? buildDockCard(overlay, baseReadings, metric, input.isMonophase) : null;
	const gridCards = cards
		.filter((card) => !dockCard || card.id !== dockCard.id)
		.sort(
			(left, right) =>
				PRIORITY_RANK[left.priority] - PRIORITY_RANK[right.priority] ||
				left.sortValue - right.sortValue,
		);

	if (overlay) {
		gridCards.forEach((card) => {
			card.disabled = true;
			card.accentColor = OVERLAY_MUTE_ACCENT;
			if (demotePrimary && card.priority === 'primary') card.priority = 'secondary';
		});
	}

	const absPhase = phaseValues.map((value) => Math.abs(value));
	const maxPhase = Math.max(...absPhase, 1);
	const phaseBars: EmeterPriorityBar[] = input.isMonophase
		? []
		: PHASES.map((phase, index) => ({
				label: phaseName(index, input.channelNames),
				progress: Math.round((absPhase[index] / maxPhase) * 100),
				value: `${formatScaled(phaseValues[index], unit).value} ${formatScaled(phaseValues[index], unit).unit}`,
				accentColor: overlay ? OVERLAY_MUTE_ACCENT : PHASE_ACCENTS[index],
			}));

	const balanced = imbalancePercent <= EMETER_IMBALANCE_THRESHOLD;
	const showImbalanceChip =
		Boolean(input.imbalanceEmphasis) || (!input.isMonophase && force === 'imbalance');

	return {
		mode,
		overlay,
		cards: gridCards,
		dockCard,
		phaseBars,
		phaseStatus: input.isMonophase
			? null
			: balanced
				? {
						label: {
							key: 'camera_nv_balanced',
							default: 'Balanced',
						},
						tone: 'success',
					}
				: showImbalanceChip
					? {
							label: {
								key: 'energy_monitoring_phase_imbalanced',
								default: 'Imbalanced {percent}%',
								values: { percent: imbalancePercent },
							},
							tone: 'warning',
						}
					: null,
		imbalancePercent,
		showPhaseBars: !input.isMonophase,
	};
};
